import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import ProgressiveImage from '../components/common/ProgressiveImage';
import { addToCart, toggleWishlist } from '../redux/slice/user.slice';
import {
    ShoppingCart, Heart, Star, Monitor, Cpu, Calendar,
    User as UserIcon, Award, ChevronLeft, ChevronRight, Share2, Download,
    Shield, Zap, Globe, MessageSquare, ThumbsUp, ThumbsDown, X, Trash2,
    HardDrive, Layers, Gamepad2, Smartphone, Play, Pause, Volume2, VolumeX, Maximize,
    ArrowRight, CheckCircle2
} from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { FreeMode, Mousewheel } from 'swiper/modules';
import { FaWindows, FaApple, FaXbox } from 'react-icons/fa';
import { DiAndroid } from 'react-icons/di';
import { SiOculus, SiPlaystation, SiSteamdeck } from 'react-icons/si';
import { BsNintendoSwitch } from 'react-icons/bs';
import { TbDeviceVisionProFilled } from 'react-icons/tb';
import { fetchGameReviews, submitReview, updateReview, clearReviewStatus, deleteReview, toggleLikeReview, toggleDislikeReview } from '../redux/slice/review.slice';
import { fetchGameById } from '../redux/slice/game.slice';
import { getGamePath } from '../utils/gamePath';
import RecentlyViewed from '../components/shop/RecentlyViewed';
import BuyNowModal from '../components/shop/BuyNowModal';
import { Helmet } from 'react-helmet-async';

const getSafeVideoUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('blob:')) return url;
    const match = url.match(/^(https?:\/\/[^\/]+)\/(.*)$/);
    if (!match) return url;
    const domain = match[1];
    const path = match[2];
    const encodedPath = path.split('/').map(segment => encodeURIComponent(segment)).join('/');
    return `${domain}/${encodedPath}`;
};

export default function GameDetails() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { games, loading, selectedGame, selectedGameLoading, platformWiseGames } = useSelector((s) => s.game || {});
    const { wishlist, wishlistDetailed, cart, recentlyViewedDetailed } = useSelector((s) => s.userSlice || {});
    const { trending, newRelease, bestSeller } = useSelector((s) => s.home || {});
    const { isAuthenticated, user: currentUser } = useSelector((s) => s.auth);
    const { reviews, loading: reviewsLoading, success: reviewSuccess, error: reviewErrorRedux } = useSelector((s) => s.review);
    const { publicSettings } = useSelector((s) => s.gateway || {});

    const isPurchasingRestricted = () => {
        if (!publicSettings) return false;
        if (publicSettings.purchasePaused) {
            if (publicSettings.purchaseStartsAt) {
                const startsAt = new Date(publicSettings.purchaseStartsAt).getTime();
                return startsAt > Date.now();
            }
            return true;
        }
        return false;
    };

    const restricted = isPurchasingRestricted();

    const isInWishlist = (gameId) => wishlist.includes(gameId);
    const [activeScreenshot, setActiveScreenshot] = useState(0);
    const [galleryMode, setGalleryMode] = useState('video');
    const [showReviewForm, setShowReviewForm] = useState(false);
    const [hoverRating, setHoverRating] = useState(0);
    const [reviewError, setReviewError] = useState('');
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [selectedPlatform, setSelectedPlatform] = useState('windows');
    const [visibleReviewsCount, setVisibleReviewsCount] = useState(2);
    const [showBuyNowModal, setShowBuyNowModal] = useState(false);

    // Video Custom Controls States & Refs
    const videoRef = useRef(null);
    const videoContainerRef = useRef(null);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [isVideoMuted, setIsVideoMuted] = useState(false);
    const [videoProgress, setVideoProgress] = useState(0);
    const [videoTime, setVideoTime] = useState('0:00');
    const [videoDuration, setVideoDuration] = useState('0:00');
    const [showVideoControls, setShowVideoControls] = useState(false);
    const [videoVolume, setVideoVolume] = useState(1);
    const [isVideoBuffering, setIsVideoBuffering] = useState(false);

    // Thumbnail Slider Refs & States
    const thumbSwiperRef = useRef(null);
    const [showLeftThumbArrow, setShowLeftThumbArrow] = useState(false);
    const [showRightThumbArrow, setShowRightThumbArrow] = useState(true);

    const handlePrevMedia = () => {
        const totalImages = game?.images?.length || 0;
        if (galleryMode === 'video') {
            if (totalImages > 0) {
                setGalleryMode('images');
                setActiveScreenshot(totalImages - 1);
            }
        } else {
            if (activeScreenshot > 0) {
                setActiveScreenshot(activeScreenshot - 1);
            } else if (game?.video?.url) {
                setGalleryMode('video');
            } else if (totalImages > 0) {
                setActiveScreenshot(totalImages - 1);
            }
        }
    };

    const handleNextMedia = () => {
        const totalImages = game?.images?.length || 0;
        if (galleryMode === 'video') {
            if (totalImages > 0) {
                setGalleryMode('images');
                setActiveScreenshot(0);
            }
        } else {
            if (activeScreenshot < totalImages - 1) {
                setActiveScreenshot(activeScreenshot + 1);
            } else if (game?.video?.url) {
                setGalleryMode('video');
            } else if (totalImages > 0) {
                setActiveScreenshot(0);
            }
        }
    };

    const togglePlayPause = () => {
        if (!videoRef.current) return;
        if (videoRef.current.paused) {
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(() => {
                    // Playback was interrupted or prevented
                });
            }
            setIsVideoPlaying(true);
        } else {
            videoRef.current.pause();
            setIsVideoPlaying(false);
        }
    };

    const toggleMute = () => {
        if (!videoRef.current) return;
        videoRef.current.muted = !videoRef.current.muted;
        setIsVideoMuted(videoRef.current.muted);
    };

    const handleVolumeSlider = (e) => {
        if (!videoRef.current) return;
        const vol = parseFloat(e.target.value);
        videoRef.current.volume = vol;
        setVideoVolume(vol);
        if (vol === 0) {
            videoRef.current.muted = true;
            setIsVideoMuted(true);
        } else {
            videoRef.current.muted = false;
            setIsVideoMuted(false);
        }
    };

    const formatTime = (time) => {
        if (isNaN(time)) return '0:00';
        const mins = Math.floor(time / 60);
        const secs = Math.floor(time % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    const handleTimeUpdate = () => {
        if (!videoRef.current) return;
        const current = videoRef.current.currentTime;
        const duration = videoRef.current.duration || 0;
        if (duration > 0) {
            setVideoProgress((current / duration) * 100);
        }
        setVideoTime(formatTime(current));
    };

    const handleLoadedMetadata = () => {
        if (!videoRef.current) return;
        setVideoDuration(formatTime(videoRef.current.duration));
    };

    const handleSeek = (e) => {
        if (!videoRef.current) return;
        const newProgress = parseFloat(e.target.value);
        const duration = videoRef.current.duration || 0;
        videoRef.current.currentTime = (newProgress / 100) * duration;
        setVideoProgress(newProgress);
    };

    const toggleFullscreen = () => {
        if (!videoContainerRef.current) return;
        if (!document.fullscreenElement) {
            if (videoContainerRef.current.requestFullscreen) {
                videoContainerRef.current.requestFullscreen();
            } else if (videoContainerRef.current.webkitRequestFullscreen) {
                videoContainerRef.current.webkitRequestFullscreen();
            } else if (videoContainerRef.current.msRequestFullscreen) {
                videoContainerRef.current.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    };

    const userReview = reviews.find(rev => (rev.user?._id || rev.user) === (currentUser?._id || currentUser?.id));

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            rating: userReview ? userReview.rating : 5,
            comment: userReview ? userReview.comment : '',
        },
        validationSchema: Yup.object({
            rating: Yup.number().min(1, 'Rating must be at least 1').max(5, 'Rating must be at most 5').required('Rating is required'),
            comment: Yup.string()
                .required('Review text is required'),
        }),
        onSubmit: (values) => {
            if (userReview) {
                dispatch(updateReview({
                    reviewId: userReview._id || userReview.id,
                    rating: values.rating,
                    comment: values.comment.trim()
                }));
            } else {
                dispatch(submitReview({
                    gameId: slug,
                    rating: values.rating,
                    comment: values.comment.trim()
                }));
            }
        }
    });

    // Instant game resolution: check selectedGame, games list, wishlistDetailed, recentlyViewedDetailed, and home games
    const toGameSlug = (title) => {
        if (!title || typeof title !== 'string') return '';
        return title
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const isMatchingSlug = (g) => {
        if (!g) return false;
        const generatedSlug = toGameSlug(g.title);
        return (
            g.slug === slug ||
            (generatedSlug && generatedSlug === slug) ||
            g._id === slug ||
            g.id === slug ||
            String(g._id || g.id) === String(slug)
        );
    };

    const allPlatformGames = useMemo(() => {
        if (!platformWiseGames) return [];
        return Object.values(platformWiseGames).flatMap(p => p?.games || []);
    }, [platformWiseGames]);

    const hasMediaLoaded = (g) => Boolean(g && (g.video !== undefined || g.images !== undefined));

    const gameFromList = games?.find(isMatchingSlug) ||
        allPlatformGames.find(isMatchingSlug) ||
        (wishlistDetailed || []).find(isMatchingSlug) ||
        (recentlyViewedDetailed || []).find(isMatchingSlug) ||
        (trending || []).find(isMatchingSlug) ||
        (newRelease || []).find(isMatchingSlug) ||
        (bestSeller || []).find(isMatchingSlug);

    const resolvedGame = (selectedGame && isMatchingSlug(selectedGame)) ? selectedGame : gameFromList;
    const isCurrentGameReady = resolvedGame && isMatchingSlug(resolvedGame) && (!selectedGameLoading || hasMediaLoaded(resolvedGame));
    const game = isCurrentGameReady ? resolvedGame : null;

    // Calculate dynamic rating metrics from reviews
    const hasReviews = reviews && reviews.length > 0;
    const totalReviewsCount = reviews.length;

    // Calculate dynamic average rating
    const averageRating = hasReviews
        ? Number((reviews.reduce((acc, rev) => acc + rev.rating, 0) / totalReviewsCount).toFixed(1))
        : (game?.rating || 5);

    // Calculate dynamic star counts and percentages
    const starCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    if (hasReviews) {
        reviews.forEach(rev => {
            const r = Math.round(rev.rating);
            if (starCounts[r] !== undefined) {
                starCounts[r]++;
            }
        });
    }

    const starPercentages = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    if (hasReviews) {
        Object.keys(starCounts).forEach(star => {
            starPercentages[star] = Math.round((starCounts[star] / totalReviewsCount) * 100);
        });
    } else {
        starPercentages[5] = 100;
    }

    const containerRef = useRef(null);
    const heroRef = useRef(null);
    const infoRef = useRef(null);
    const trackedSlugRef = useRef(null);

    // Fetch the single game and its reviews once per slug change
    useEffect(() => {
        if (slug) {
            dispatch(fetchGameById(slug));
            dispatch(fetchGameReviews(slug));
            setVisibleReviewsCount(2);
            setActiveScreenshot(0);
            setGalleryMode('video');
            window.scrollTo(0, 0);
        }
    }, [slug, dispatch]);

    // Automatically sync selectedPlatform to the game's actual available platform
    useEffect(() => {
        if (game?.platforms) {
            const availableKeys = Object.keys(game.platforms).filter(k => game.platforms[k]?.available);
            if (availableKeys.length > 0) {
                if (!availableKeys.includes(selectedPlatform)) {
                    setSelectedPlatform(availableKeys[0]);
                }
            }
        }
    }, [game, selectedPlatform]);

    // Keyboard Shortcuts for Video Controls
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Only capture if active gallery mode is video
            if (galleryMode !== 'video') return;

            // Ignore shortcuts if the user is currently typing in an input element
            const activeTag = document.activeElement?.tagName?.toLowerCase();
            if (activeTag === 'input' || activeTag === 'textarea' || document.activeElement?.isContentEditable) {
                return;
            }

            const key = e.key.toLowerCase();

            if (key === 'k' || e.key === ' ') {
                e.preventDefault();
                togglePlayPause();
            } else if (key === 'm') {
                e.preventDefault();
                toggleMute();
            } else if (key === 'f') {
                e.preventDefault();
                toggleFullscreen();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [galleryMode, isVideoPlaying, isVideoMuted]);

    useEffect(() => {
        if (thumbSwiperRef.current && !thumbSwiperRef.current.destroyed) {
            const hasVideo = Boolean(game?.video?.url);
            const targetIndex = hasVideo ? (galleryMode === 'video' ? 0 : activeScreenshot + 1) : activeScreenshot;
            thumbSwiperRef.current.slideTo(targetIndex);
        }
    }, [activeScreenshot, galleryMode, game]);

    useEffect(() => {
        if (!game) return;

        const available = Object.keys(game.platforms || {}).filter(
            plat => game.platforms[plat]?.available
        );
        if (available.length > 0) {
            if (available.includes('windows')) {
                setSelectedPlatform('windows');
            } else {
                setSelectedPlatform(available[0]);
            }
        }

        // Dispatch custom event to track recently viewed and set initial gallery mode ONCE per slug
        if (slug && trackedSlugRef.current !== slug) {
            const gameId = game._id || game.id;
            if (gameId) {
                trackedSlugRef.current = slug;
                if (game.video?.url) {
                    setGalleryMode('video');
                } else if (game.images && game.images.length > 0) {
                    setGalleryMode('images');
                }
                window.dispatchEvent(new CustomEvent('cgGameOpened', { detail: { id: gameId } }));
            }
        }
    }, [game, slug]);

    // Redirect legacy ObjectId URLs to slug-based URLs
    useEffect(() => {
        if (!selectedGame?.slug || !slug) return;
        const isLegacyId = /^[a-f\d]{24}$/i.test(slug);
        const matchesLoadedGame = slug === String(selectedGame._id || selectedGame.id);
        if (isLegacyId && matchesLoadedGame && selectedGame.slug !== slug) {
            navigate(getGamePath(selectedGame), { replace: true });
        }
    }, [selectedGame, slug, navigate]);

    // Handle Video Autoplay and Interruption
    useEffect(() => {
        if (galleryMode === 'video' && videoRef.current && isVideoPlaying) {
            const playPromise = videoRef.current.play();
            if (playPromise !== undefined) {
                playPromise.catch(() => {
                    // Silently handle interruption error
                });
            }
        }
    }, [galleryMode, isVideoPlaying]);

    useEffect(() => {
        if (reviewSuccess) {
            setShowReviewForm(false);
            formik.resetForm();
            setHoverRating(0);
            toast.success("Review saved successfully!");
            dispatch(clearReviewStatus());
        }
        if (reviewErrorRedux) {
            setReviewError(reviewErrorRedux);
            toast.error(reviewErrorRedux);
            dispatch(clearReviewStatus());
        }
    }, [reviewSuccess, reviewErrorRedux, dispatch]);

    if (!game) {
        return (
            <div className="min-h-screen bg-[#050508] text-white flex flex-col items-center justify-center gap-4">
                <div className="w-12 h-12 rounded-full border-4 border-cyber-cyan/20 border-t-cyber-cyan animate-spin shadow-[0_0_20px_rgba(0,240,255,0.3)]"></div>
                <span className="text-xs font-cyber tracking-widest text-cyber-cyan uppercase animate-pulse">Loading Game Stream...</span>
            </div>
        );
    }
    const gameId = game._id || game.id;
    const gameSlug = game.slug || gameId;
    const isFav = isInWishlist(gameId);
    const isInCart = Boolean(cart?.some(item =>
        (item.game?._id === gameId || item.game?.id === gameId || item.id === gameId || item._id === gameId) &&
        (!item.platform || !selectedPlatform || item.platform === selectedPlatform)
    ));
    const relatedGames = games?.filter((g) => {
        const currentId = g._id || g.id;
        if (currentId === gameId) return false;
        const gCatId = g.category?._id || g.category;
        const gameCatId = game.category?._id || game.category;
        return gCatId && gameCatId && gCatId.toString() === gameCatId.toString();
    }).slice(0, 4);

    const firstPlatformKey = game ? Object.keys(game.platforms || {}).find(k => game.platforms[k]?.available) : null;
    const gamePrice = game
        ? (game.platforms?.[selectedPlatform]?.price ?? game.platforms?.[firstPlatformKey]?.price ?? game.price ?? 0)
        : 0;

    const schemaOrgJson = game ? {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": game.title,
        "description": game.description || game.tagline,
        "applicationCategory": "GameApplication",
        "operatingSystem": Object.keys(game.platforms || {})
            .filter(k => game.platforms[k]?.available)
            .join(", "),
        "image": game.cover_image?.url || game.image,
        "offers": {
            "@type": "Offer",
            "price": gamePrice,
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "url": `https://cubegames.ca/game/${gameSlug}`
        },
        "aggregateRating": hasReviews ? {
            "@type": "AggregateRating",
            "ratingValue": averageRating,
            "reviewCount": totalReviewsCount
        } : {
            "@type": "AggregateRating",
            "ratingValue": 5,
            "reviewCount": 1
        }
    } : null;

    return (
        <div className="min-h-screen bg-transparent text-white selection:bg-cyber-cyan/30 font-cyber">
            {game && (
                <Helmet>
                    <title>{`${game.title} | Buy & Download at CUBE GAMES`}</title>
                    <meta name="description" content={game.tagline || (game.description ? game.description.slice(0, 160) : "")} />
                    <meta name="keywords" content={`${game.title}, buy ${game.title}, download ${game.title}, ${game.category?.name || ""} game, cheap keys`} />
                    <link rel="canonical" href={`https://cubegames.ca/game/${gameSlug}`} />

                    {/* Open Graph */}
                    <meta property="og:type" content="books.book" />
                    <meta property="og:title" content={`${game.title} | Buy & Download at CUBE GAMES`} />
                    <meta property="og:description" content={game.tagline || (game.description ? game.description.slice(0, 160) : "")} />
                    <meta property="og:image" content={game.cover_image?.url || game.image} />
                    <meta property="og:url" content={`https://cubegames.ca/game/${gameSlug}`} />

                    {/* Twitter */}
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={`${game.title} | Buy & Download at CUBE GAMES`} />
                    <meta name="twitter:description" content={game.tagline || (game.description ? game.description.slice(0, 160) : "")} />
                    <meta name="twitter:image" content={game.cover_image?.url || game.image} />

                    {/* JSON-LD Schema */}
                    <script type="application/ld+json">
                        {JSON.stringify(schemaOrgJson)}
                    </script>
                </Helmet>
            )}
            {/* Delete Confirmation Modal */}
            {deleteConfirmId && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
                    <div className="w-full max-w-md bg-[#0c0c18] border border-white/10 rounded-[1.5rem] p-8 shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyber-pink to-cyber-purple"></div>
                        <div className="text-center space-y-6">
                            <div className="w-20 h-20 bg-cyber-pink/10 rounded-3xl flex items-center justify-center mx-auto border border-cyber-pink/20">
                                <Trash2 className="w-10 h-10 text-cyber-pink" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-2xl font-cyber font-black text-white uppercase tracking-tight">Delete Review?</h3>
                                <p className="text-gray-400 text-sm leading-relaxed">Are you sure you want to delete this review? This action cannot be undone.</p>
                            </div>
                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => setDeleteConfirmId(null)}
                                    className="flex-1 py-4 rounded-2xl bg-white/5 border border-white/10 text-gray-400 font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-white/10 hover:text-white transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        dispatch(deleteReview(deleteConfirmId));
                                        setDeleteConfirmId(null);
                                        toast.success("Review deleted successfully");
                                    }}
                                    className="flex-1 py-4 rounded-2xl bg-cyber-pink/20 border border-cyber-pink/40 text-cyber-pink font-cyber font-bold text-[10px] uppercase tracking-widest hover:bg-cyber-pink/30 shadow-[0_0_20px_rgba(255,0,122,0.1)] transition-all"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Hero Section */}
            <div className="relative h-[52vh] sm:h-[60vh] lg:h-[65vh] min-h-[380px] sm:min-h-[450px] w-full overflow-hidden">
                <div className="absolute inset-0">
                    <ProgressiveImage
                        src={game.cover_image?.url || game.image}
                        placeholderSrc={game.cover_image?.lowUrl || game.imageLow}
                        alt={game.title}
                        className="w-full h-full object-cover object-center"
                    />
                    {/* Top dark gradient overlay for crystal clear header/navbar visibility */}
                    <div className="absolute inset-x-0 top-0 h-44 sm:h-60 bg-gradient-to-b from-black/95 via-black/60 to-transparent pointer-events-none z-[1]"></div>
                    <div className="absolute inset-0 bg-black/40"></div>
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#030305]"></div>
                </div>

                <div className="absolute inset-0 flex flex-col justify-end px-4 sm:px-6 lg:px-8 pb-6 sm:pb-10 lg:pb-12 container mx-auto w-full">
                    <button
                        onClick={() => navigate(-1)}
                        className="group inline-flex items-center gap-2.5 mb-2.5 sm:mb-4 w-fit px-4 py-2 rounded-xl bg-[#0a0c16]/80 hover:bg-[#00f0ff]/10 border border-white/10 hover:border-[#00f0ff]/60 text-gray-300 hover:text-white text-xs font-cyber font-bold tracking-widest uppercase transition-all duration-300 backdrop-blur-md shadow-lg hover:shadow-[0_0_20px_rgba(0,240,255,0.3)] active:scale-95"
                    >
                        <ChevronLeft className="w-4 h-4 text-[#00f0ff] transition-transform duration-300 group-hover:-translate-x-1" />
                        <span>BACK</span>
                    </button>

                    <div className="space-y-2 sm:space-y-3">
                        <div className="flex items-center gap-2 sm:gap-2.5 flex-wrap">
                            {game.category?.name && (
                                <span className="px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full bg-cyber-cyan/20 border border-cyber-cyan/50 text-cyber-cyan text-[11px] sm:text-xs font-cyber font-bold tracking-wider uppercase backdrop-blur-md">
                                    {game.category.name}
                                </span>
                            )}
                            {game.isVRReady && (
                                <span className="px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full bg-cyber-purple/20 border border-cyber-purple/50 text-cyber-purple text-[11px] sm:text-xs font-cyber font-bold tracking-wider uppercase flex items-center gap-1.5 backdrop-blur-md">
                                    <Cpu className="w-3.5 h-3.5" /> VR READY
                                </span>
                            )}
                        </div>
                        <h1 className="text-2xl sm:text-4xl lg:text-6xl font-cyber font-black tracking-wider leading-tight uppercase text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.8)]">
                            {game.title.split(':').map((part, i) => (
                                <span key={i} className={i === 1 ? "block text-transparent bg-clip-text bg-gradient-to-r from-cyber-cyan to-cyber-blue" : "block text-white"}>
                                    {part}{i === 0 && game.title.includes(':') ? ':' : ''}
                                </span>
                            ))}
                        </h1>
                        {(game.tagline || game.description) && (
                            <p className="max-w-2xl text-gray-300 text-xs sm:text-sm font-cyber font-medium tracking-wide leading-relaxed drop-shadow-[0_1px_5px_rgba(0,0,0,0.8)] line-clamp-2 sm:line-clamp-none">
                                {game.tagline || (game.description?.length > 160 ? `${game.description.substring(0, 160)}...` : game.description)}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="relative z-10 px-4 sm:px-6 lg:px-8 container mx-auto -mt-2 sm:-mt-6 lg:-mt-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-12 lg:gap-y-16 gap-x-8 xl:gap-x-12">

                    {/* Top Content: Gallery & Overview (Order 1 on Mobile/Tablet) */}
                    <div className="order-1 lg:col-span-7 xl:col-span-8 lg:col-start-1 lg:row-start-1 space-y-10 sm:space-y-14">

                        {/* Gallery / Video Player */}
                        <section className="space-y-4">
                            {/* Tabs for switching */}
                            {game.video?.url && (
                                <div className="flex items-center gap-3 sm:gap-6 border-b border-white/5 pt-2 pb-2 overflow-x-auto no-scrollbar whitespace-nowrap">
                                    <button
                                        type="button"
                                        onClick={() => setGalleryMode('video')}
                                        className={`px-1 py-1 text-sm sm:text-base font-cyber font-bold tracking-wider transition-all cursor-pointer ${galleryMode === 'video' ? 'text-cyber-cyan border-b-2 border-cyber-cyan' : 'text-gray-500 hover:text-white'}`}
                                    >
                                        TRAILER
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setGalleryMode('images');
                                            if (game.images?.length > 0 && activeScreenshot >= game.images.length) {
                                                setActiveScreenshot(0);
                                            }
                                        }}
                                        className={`px-1 py-1 text-sm sm:text-base font-cyber font-bold tracking-wider transition-all cursor-pointer ${galleryMode === 'images' ? 'text-cyber-cyan border-b-2 border-cyber-cyan' : 'text-gray-500 hover:text-white'}`}
                                    >
                                        SCREENSHOTS ({game.images?.length || 0})
                                    </button>
                                </div>
                            )}

                            <div className="relative aspect-video w-full rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10 shadow-2xl bg-black/60 group/player">
                                {/* Navigation Arrows Overlay */}
                                {((game.images?.length > 1) || (game.video?.url && game.images?.length > 0)) && (
                                    <>
                                        <button
                                            onClick={handlePrevMedia}
                                            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 p-2.5 rounded-full bg-black/60 border border-white/10 text-white hover:border-cyber-cyan hover:bg-cyber-cyan/20 hover:text-cyber-cyan transition-all shadow-lg backdrop-blur-sm group/btn"
                                            title="Previous"
                                        >
                                            <ChevronLeft className="w-5 h-5 group-hover/btn:-translate-x-0.5 transition-transform" />
                                        </button>
                                        <button
                                            onClick={handleNextMedia}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-2.5 rounded-full bg-black/60 border border-white/10 text-white hover:border-cyber-cyan hover:bg-cyber-cyan/20 hover:text-cyber-cyan transition-all shadow-lg backdrop-blur-sm group/btn"
                                            title="Next"
                                        >
                                            <ChevronRight className="w-5 h-5 group-hover/btn:translate-x-0.5 transition-transform" />
                                        </button>
                                    </>
                                )}

                                {/* Media Badge Counter */}
                                <div className="absolute top-4 right-4 z-20 px-3 py-1 rounded-full bg-black/60 border border-white/10 text-gray-300 text-[10px] font-cyber tracking-widest uppercase backdrop-blur-sm">
                                    {galleryMode === 'video' ? 'TRAILER' : `${activeScreenshot + 1} / ${game.images?.length || 1}`}
                                </div>

                                {galleryMode === 'video' && game.video?.url ? (
                                    <div
                                        ref={videoContainerRef}
                                        className="relative w-full h-full group bg-black"
                                        onMouseEnter={() => setShowVideoControls(true)}
                                        onMouseLeave={() => setShowVideoControls(false)}
                                    >
                                        <video
                                            ref={videoRef}
                                            src={getSafeVideoUrl(game.video.url)}
                                            loop
                                            preload="auto"
                                            playsInline
                                            webkit-playsinline="true"
                                            muted={isVideoMuted}
                                            onTimeUpdate={handleTimeUpdate}
                                            onLoadedMetadata={handleLoadedMetadata}
                                            onClick={togglePlayPause}
                                            onWaiting={() => setIsVideoBuffering(true)}
                                            onPlaying={() => setIsVideoBuffering(false)}
                                            onSeeking={() => setIsVideoBuffering(true)}
                                            onSeeked={() => setIsVideoBuffering(false)}
                                            onCanPlay={() => setIsVideoBuffering(false)}
                                            onCanPlayThrough={() => setIsVideoBuffering(false)}
                                            className="w-full h-full object-contain cursor-pointer"
                                            poster={game.cover_image?.url || game.image}
                                        />

                                        {/* Buffering Spinner */}
                                        {isVideoBuffering && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
                                                <div className="w-12 h-12 rounded-full border-4 border-cyber-pink border-t-transparent animate-spin shadow-[0_0_15px_rgba(255,0,122,0.3)]"></div>
                                            </div>
                                        )}

                                        {/* Play Overlay */}
                                        {!isVideoPlaying && !isVideoBuffering && (
                                            <div
                                                onClick={togglePlayPause}
                                                className="absolute inset-0 flex items-center justify-center bg-black/40 cursor-pointer transition-all duration-300"
                                            >
                                                <button className="p-4 rounded-full bg-cyber-pink/20 border border-cyber-pink/40 text-cyber-pink shadow-cyber-pink transition-transform transform hover:scale-110">
                                                    <Play className="w-8 h-8 fill-cyber-pink" />
                                                </button>
                                            </div>
                                        )}

                                        {/* Controls Overlay */}
                                        <div className={`absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/95 via-black/70 to-transparent flex flex-col gap-2.5 transition-all duration-300 ${showVideoControls ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}>

                                            {/* Progress Timeline */}
                                            <div className="flex items-center gap-3">
                                                <span className="text-[10px] font-cyber text-gray-400 min-w-[32px]">{videoTime}</span>
                                                <div className="relative flex-1 group/slider">
                                                    <input
                                                        type="range"
                                                        min="0"
                                                        max="100"
                                                        value={videoProgress}
                                                        onChange={handleSeek}
                                                        className="w-full h-1 bg-white/20 rounded-lg appearance-none cursor-pointer accent-cyber-blue group-hover/slider:h-1.5 transition-all outline-none"
                                                        style={{
                                                            background: `linear-gradient(to right, #00f0ff 0%, #00f0ff ${videoProgress}%, rgba(255,255,255,0.2) ${videoProgress}%, rgba(255,255,255,0.2) 100%)`
                                                        }}
                                                    />
                                                </div>
                                                <span className="text-[10px] font-cyber text-gray-400 min-w-[32px] text-right">{videoDuration}</span>
                                            </div>

                                            {/* Control Buttons */}
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <button onClick={togglePlayPause} className="text-white hover:text-cyber-blue transition-colors">
                                                        {isVideoPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 fill-white" />}
                                                    </button>

                                                    <div className="flex items-center gap-2 group/volume">
                                                        <button onClick={toggleMute} className="text-white hover:text-cyber-blue transition-colors">
                                                            {isVideoMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                                                        </button>
                                                        <input
                                                            type="range"
                                                            min="0"
                                                            max="1"
                                                            step="0.05"
                                                            value={isVideoMuted ? 0 : videoVolume}
                                                            onChange={handleVolumeSlider}
                                                            className="w-16 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer accent-cyber-blue transition-all outline-none"
                                                        />
                                                    </div>
                                                </div>

                                                <button onClick={toggleFullscreen} className="text-white hover:text-cyber-blue transition-colors">
                                                    <Maximize className="w-4 h-4" />
                                                </button>
                                            </div>

                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <img
                                            src={game.images?.[activeScreenshot]?.url || (typeof game.images?.[activeScreenshot] === 'string' ? game.images[activeScreenshot] : null) || game.cover_image?.url || game.image}
                                            alt={game.title || 'Screenshot'}
                                            className="w-full h-full object-cover animate-fade-in"
                                            key={activeScreenshot}
                                        />
                                        {game.images?.length > 1 && (
                                            <div className="absolute bottom-6 left-6 flex gap-2 z-10">
                                                {game.images.map((_, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => setActiveScreenshot(i)}
                                                        className={`w-3 h-3 rounded-full transition-all ${activeScreenshot === i ? 'bg-cyber-cyan w-8' : 'bg-white/20 hover:bg-white/40'}`}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Thumbnails Swiper Carousel Slider */}
                            <div className="relative group/thumbs pt-1 px-1">
                                {showLeftThumbArrow && (
                                    <button
                                        onClick={() => thumbSwiperRef.current?.slidePrev()}
                                        className="absolute left-0 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-black/80 border border-cyber-cyan/40 text-cyber-cyan shadow-[0_0_15px_rgba(0,240,255,0.3)] hover:scale-110 active:scale-95 transition-all backdrop-blur-md"
                                        title="Scroll Left"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                )}

                                {showRightThumbArrow && (
                                    <button
                                        onClick={() => thumbSwiperRef.current?.slideNext()}
                                        className="absolute right-0 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-black/80 border border-cyber-cyan/40 text-cyber-cyan shadow-[0_0_15px_rgba(0,240,255,0.3)] hover:scale-110 active:scale-95 transition-all backdrop-blur-md"
                                        title="Scroll Right"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                )}

                                <Swiper
                                    onBeforeInit={(swiper) => {
                                        thumbSwiperRef.current = swiper;
                                    }}
                                    onSlideChange={(swiper) => {
                                        setShowLeftThumbArrow(!swiper.isBeginning);
                                        setShowRightThumbArrow(!swiper.isEnd);
                                    }}
                                    onInit={(swiper) => {
                                        setShowLeftThumbArrow(!swiper.isBeginning);
                                        setShowRightThumbArrow(!swiper.isEnd);
                                    }}
                                    modules={[FreeMode, Mousewheel]}
                                    freeMode={true}
                                    mousewheel={{ forceToAxis: true }}
                                    spaceBetween={12}
                                    slidesPerView={3}
                                    breakpoints={{
                                        480: { slidesPerView: 3.5, spaceBetween: 12 },
                                        640: { slidesPerView: 4.5, spaceBetween: 14 },
                                        768: { slidesPerView: 5, spaceBetween: 16 },
                                        1024: { slidesPerView: 5.5, spaceBetween: 16 },
                                    }}
                                    className="!py-3 !px-1.5"
                                >
                                    {game.video?.url && (
                                        <SwiperSlide key="video-thumb" className="!p-0.5">
                                            <div
                                                onClick={() => setGalleryMode('video')}
                                                className={`aspect-video md:rounded-xl rounded-lg overflow-hidden border-2 cursor-pointer transition-all relative flex items-center justify-center ${galleryMode === 'video' ? 'border-[#c026d3] shadow-[0_0_15px_rgba(192,38,211,0.5)] ring-1 ring-[#c026d3]/40' : 'border-white/10 opacity-70 hover:opacity-100 hover:border-white/30'}`}
                                            >
                                                <ProgressiveImage src={game.cover_image?.url || game.image} placeholderSrc={game.cover_image?.lowUrl || game.image} alt="Video Poster" className="w-full h-full object-cover" />
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                                    <div className="w-7 h-7 md:w-9 md:h-9 rounded-full bg-cyber-purple/90 hover:bg-cyber-purple flex items-center justify-center shadow-[0_0_12px_rgba(189,0,255,0.6)] transition-all">
                                                        <span className="text-white text-[10px] md:text-xs pl-0.5">▶</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </SwiperSlide>
                                    )}
                                    {game.images?.map((img, i) => (
                                        <SwiperSlide key={i} className="!p-0.5">
                                            <div
                                                onClick={() => { setGalleryMode('images'); setActiveScreenshot(i); }}
                                                className={`aspect-video md:rounded-xl rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${galleryMode === 'images' && activeScreenshot === i ? 'border-cyber-cyan shadow-[0_0_15px_rgba(0,240,255,0.5)] ring-1 ring-cyber-cyan/40' : 'border-white/10 opacity-60 hover:opacity-100 hover:border-white/30'}`}
                                            >
                                                <ProgressiveImage src={img.url || img} placeholderSrc={img.lowUrl || img.url || img} alt={`Screenshot ${i + 1}`} className="w-full h-full object-cover" />
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                        </section>

                        {/* About */}
                        <section className="reveal space-y-4">
                            <div className="flex items-center gap-3 md:gap-4">
                                <h2 className="text-lg lg:text-2xl font-cyber font-black uppercase tracking-tight text-white">OVERVIEW</h2>
                                <div className="h-px w-16 sm:w-32 bg-gradient-to-r from-white/10 to-transparent"></div>
                            </div>
                            <div className="relative rounded-2xl sm:rounded-3xl bg-gradient-to-b from-[#0a0c20]/90 via-[#070818]/90 to-[#04040e]/95 border border-white/10 p-5 sm:p-7 shadow-2xl backdrop-blur-xl overflow-hidden group/overview">
                                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyber-cyan/40 to-transparent" />
                                <div className="absolute -top-12 -right-12 w-32 h-32 bg-cyber-cyan/10 rounded-full blur-3xl pointer-events-none" />
                                <p className="text-gray-300 leading-relaxed text-sm sm:text-base font-normal tracking-wide relative z-10 whitespace-pre-line">
                                    {game.description || game.synopsis}
                                </p>
                            </div>
                        </section>
                    </div>

                    {/* Bottom Content: System Requirements & Reviews (Order 3 on Mobile/Tablet) */}
                    <div className="order-3 lg:order-none lg:col-span-7 xl:col-span-8 lg:col-start-1 lg:row-start-2 space-y-12 sm:space-y-16 pb-6 lg:pb-24">

                        {/* Specs */}
                        <section className="reveal space-y-4 pt-4">
                            <div className="flex flex-wrap flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 w-full mb-1">
                                <div className="flex items-center gap-3 shrink-0">
                                    <h2 className="text-base sm:text-lg md:text-xl lg:text-2xl font-cyber font-black uppercase tracking-tight text-white whitespace-nowrap">SYSTEM REQUIREMENTS</h2>
                                </div>

                                {/* System Requirements Platform Tabs */}
                                {(() => {
                                    const allSpecTabs = [
                                        { key: 'windows', label: 'PC' },
                                        { key: 'playstation', label: 'PLAYSTATION' },
                                        { key: 'xbox', label: 'XBOX' },
                                        { key: 'nintendo', label: 'NINTENDO' },
                                        { key: 'quest', label: 'QUEST VR' },
                                        { key: 'android', label: 'ANDROID' },
                                        { key: 'ios', label: 'IOS' },
                                        { key: 'visionpro', label: 'VISION PRO' },
                                        { key: 'steamdeck', label: 'STEAM DECK' }
                                    ];
                                    // Only show tabs for platforms that are actually available for this game
                                    const availablePlatformKeys = Object.keys(game?.platforms || {}).filter(k => game?.platforms?.[k]?.available);
                                    const specTabs = availablePlatformKeys.length > 0
                                        ? allSpecTabs.filter(tab => availablePlatformKeys.includes(tab.key))
                                        : allSpecTabs.filter(tab => !!game?.platforms?.[tab.key]);

                                    if (specTabs.length <= 1) {
                                        return specTabs.length === 1 ? (
                                            <span className="w-fit self-start md:self-auto inline-flex items-center text-xs font-cyber font-bold text-cyber-cyan px-3.5 py-1 sm:py-1.5 rounded-full bg-cyber-cyan/10 border border-cyber-cyan shadow-[0_0_12px_rgba(0,240,255,0.2)] shrink-0">
                                                {specTabs[0].label}
                                            </span>
                                        ) : null;
                                    }

                                    return (
                                        <div className="flex whitespace-nowrap overflow-x-auto no-scrollbar items-center gap-1 sm:gap-1.5 p-1 rounded-2xl sm:rounded-full bg-[#050614]/95 border border-white/10 shadow-inner backdrop-blur-md max-w-full w-fit">
                                            {specTabs.map((p) => {
                                                const activeKey = selectedPlatform || specTabs[0]?.key;
                                                const isTabActive = activeKey === p.key;
                                                return (
                                                    <button
                                                        key={p.key}
                                                        type="button"
                                                        onClick={() => setSelectedPlatform(p.key)}
                                                        className={`px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-cyber font-bold tracking-wider uppercase transition-all duration-200 shrink-0 ${isTabActive
                                                            ? 'border-2 border-cyber-cyan bg-gradient-to-r from-[#041d2c]/90 via-[#0a0f24]/95 to-[#180a24]/90 text-white shadow-[0_0_15px_rgba(0,240,255,0.35)] font-black'
                                                            : 'border border-transparent text-gray-400 hover:text-white hover:bg-white/5'
                                                            }`}
                                                    >
                                                        {p.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>

                            <div className="space-y-2.5 sm:space-y-3">
                                {(() => {
                                    const availablePlatformKeys = Object.keys(game?.platforms || {}).filter(k => game?.platforms?.[k]?.available);
                                    const fallbackPlat = availablePlatformKeys.length > 0 ? availablePlatformKeys[0] : (Object.keys(game?.platforms || {})[0] || 'windows');
                                    const currentPlat = (selectedPlatform && game?.platforms?.[selectedPlatform]) ? selectedPlatform : fallbackPlat;
                                    const reqs = game?.platforms?.[currentPlat]?.system_requirements
                                        || game?.platforms?.[fallbackPlat]?.system_requirements
                                        || game?.system_requirements
                                        || {};

                                    let specs = [];

                                    if (currentPlat === 'playstation' || currentPlat === 'xbox') {
                                        specs = [
                                            { label: 'CONSOLE MODEL', value: reqs.console || (currentPlat === 'playstation' ? 'PlayStation 4 / PlayStation 5' : 'Xbox One / Xbox Series X|S'), subLabel: 'TARGET HARDWARE SYSTEM', icon: <Gamepad2 className="w-5 h-5 text-cyber-cyan" />, color: 'text-cyber-cyan' },
                                            { label: 'FIRMWARE VERSION', value: reqs.firmware || 'Latest System Software Required', subLabel: 'REQUIRED SYSTEM SOFTWARE', icon: <Cpu className="w-5 h-5 text-cyber-purple" />, color: 'text-cyber-purple' },
                                            { label: 'STORAGE CAPACITY', value: reqs.storage || '50 GB available space', subLabel: 'PHYSICAL STORAGE SPACE', icon: <HardDrive className="w-5 h-5 text-cyber-pink" />, color: 'text-cyber-pink' },
                                            { label: 'SUBSCRIPTION', value: reqs.subscription || 'Online network supported', subLabel: 'ONLINE NETWORK PASS', icon: <Zap className="w-5 h-5 text-cyber-green" />, color: 'text-cyber-green' }
                                        ];
                                    } else if (currentPlat === 'nintendo') {
                                        specs = [
                                            { label: 'CONSOLE MODEL', value: reqs.console || 'Nintendo Switch / Switch OLED', subLabel: 'TARGET HARDWARE SYSTEM', icon: <Gamepad2 className="w-5 h-5 text-cyber-cyan" />, color: 'text-cyber-cyan' },
                                            { label: 'FIRMWARE VERSION', value: reqs.firmware || 'Latest System Software Required', subLabel: 'REQUIRED SYSTEM SOFTWARE', icon: <Cpu className="w-5 h-5 text-cyber-purple" />, color: 'text-cyber-purple' },
                                            { label: 'STORAGE CAPACITY', value: reqs.storage || '30 GB available space', subLabel: 'PHYSICAL STORAGE SPACE', icon: <HardDrive className="w-5 h-5 text-cyber-pink" />, color: 'text-cyber-pink' }
                                        ];
                                    } else if (currentPlat === 'windows' || currentPlat === 'steamdeck') {
                                        specs = [
                                            { label: 'OPERATING SYSTEM', value: reqs.os || 'Windows 10 / 11 (64-bit)', subLabel: 'PLATFORM ARCHITECTURE', icon: <Monitor className="w-5 h-5 text-cyber-cyan" />, color: 'text-cyber-cyan' },
                                            { label: 'PROCESSOR', value: reqs.processor || reqs.cpu || 'Intel Core i5-8400 / AMD Ryzen 5 2600', subLabel: 'CENTRAL PROCESSING UNIT', icon: <Cpu className="w-5 h-5 text-cyber-purple" />, color: 'text-cyber-purple' },
                                            { label: 'MEMORY (RAM)', value: reqs.memory || reqs.ram || '8 GB RAM or higher', subLabel: 'RANDOM ACCESS MEMORY', icon: <Zap className="w-5 h-5 text-cyber-green" />, color: 'text-cyber-green' },
                                            { label: 'GRAPHICS CARD', value: reqs.graphics || reqs.gpu || 'NVIDIA GeForce GTX 1060 / AMD RX 580', subLabel: 'GRAPHICS PROCESSING UNIT', icon: <Layers className="w-5 h-5 text-cyber-blue" />, color: 'text-cyber-blue' },
                                            { label: 'STORAGE CAPACITY', value: reqs.storage || reqs.hdd || '50 GB available space', subLabel: 'PHYSICAL STORAGE SPACE', icon: <HardDrive className="w-5 h-5 text-cyber-pink" />, color: 'text-cyber-pink' }
                                        ];
                                    } else {
                                        specs = [
                                            { label: 'OPERATING SYSTEM', value: reqs.os || `${currentPlat.toUpperCase()} Compatible OS`, subLabel: 'PLATFORM ARCHITECTURE', icon: <Monitor className="w-5 h-5 text-cyber-cyan" />, color: 'text-cyber-cyan' },
                                            { label: 'PROCESSOR / CHIP', value: reqs.processor || 'Hardware Acceleration Required', subLabel: 'PROCESSING UNIT', icon: <Cpu className="w-5 h-5 text-cyber-purple" />, color: 'text-cyber-purple' },
                                            { label: 'STORAGE CAPACITY', value: reqs.storage || '30 GB available space', subLabel: 'PHYSICAL STORAGE SPACE', icon: <HardDrive className="w-5 h-5 text-cyber-pink" />, color: 'text-cyber-pink' }
                                        ];
                                    }

                                    return specs.map((spec, i) => (
                                        <div
                                            key={i}
                                            className="flex flex-wrap flex-col md:flex-row md:items-center justify-between gap-2.5 sm:gap-6 p-3.5 sm:p-4 md:p-5 rounded-2xl bg-[#080918]/85 border border-white/5 hover:border-white/15 transition-all shadow-lg backdrop-blur-md"
                                        >
                                            <div className="flex  items-center gap-3 sm:gap-4 min-w-0">
                                                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#040510] border border-white/10 flex items-center justify-center shrink-0 shadow-inner">
                                                    {spec.icon}
                                                </div>
                                                <div className="min-w-0">
                                                    <span className="text-[10px] sm:text-xs font-cyber font-bold text-gray-400 tracking-wider block uppercase leading-tight">{spec.label}</span>
                                                    <span className="text-white text-xs sm:text-sm font-cyber font-bold tracking-wider block uppercase leading-tight mt-0.5">{spec.subLabel}</span>
                                                </div>
                                            </div>
                                            <div className="w-full sm:w-auto text-left pl-[52px]">
                                                <span className={`text-xs sm:text-sm md:text-base font-cyber font-black tracking-wider uppercase leading-snug break-words ${spec.color || 'text-gray-200'}`}>
                                                    {spec.value}
                                                </span>
                                            </div>
                                        </div>
                                    ));
                                })()}
                            </div>
                        </section>

                        {/* Reviews Section */}
                        <section className="reveal space-y-5 md:space-y-8 pt-4 md:pt-8 ">
                            <div className="flex flex-wrap flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-6">
                                <div className="flex items-center gap-3 md:gap-4">
                                    <h2 className="text-lg md:text-2xl font-cyber font-black uppercase tracking-tight text-white whitespace-nowrap">PLAYER REVIEWS</h2>
                                    <div className="h-px w-16 sm:w-32 bg-gradient-to-r from-white/10 to-transparent"></div>
                                </div>
                                {isAuthenticated ? (
                                    game?.isPurchased ? (
                                        <div className="flex flex-wrap items-center gap-3">
                                            {userReview && (
                                                <span className={`text-[9px] font-cyber tracking-widest uppercase px-2.5 py-1 rounded ${userReview.status === 'Published'
                                                    ? 'text-cyber-green bg-cyber-green/5 border border-cyber-green/20'
                                                    : userReview.status === 'Pending'
                                                        ? 'text-yellow-500 bg-yellow-500/5 border border-yellow-500/20'
                                                        : 'text-cyber-pink bg-cyber-pink/5 border border-cyber-pink/20'
                                                    }`}>
                                                    REVIEW {userReview.status}
                                                </span>
                                            )}
                                            <button
                                                onClick={() => {
                                                    if (showReviewForm) {
                                                        formik.resetForm();
                                                        setHoverRating(0);
                                                    }
                                                    setShowReviewForm(prev => !prev);
                                                }}
                                                className="text-white hover:text-cyber-cyan text-[10px] font-cyber font-bold uppercase tracking-widest flex items-center gap-1.5 transition-colors">
                                                <MessageSquare className="w-3.5 h-3.5" />
                                                {showReviewForm ? 'CANCEL' : (userReview ? 'EDIT YOUR REVIEW' : 'WRITE A REVIEW')}
                                            </button>
                                        </div>
                                    ) : (
                                        <span className="text-gray-300 whitespace-nowrap text-[10px] font-cyber font-bold uppercase tracking-widest hover:text-cyber-cyan flex items-center gap-1.5 transition-colors">
                                            <MessageSquare className="w-3.5 h-3.5" /> PURCHASE GAME TO REVIEW
                                        </span>
                                    )
                                ) : (
                                    <button
                                        onClick={() => navigate('/login')}
                                        className="text-gray-300 whitespace-nowrap text-[10px] font-cyber font-bold uppercase tracking-widest hover:text-cyber-cyan flex items-center gap-1.5 transition-colors">
                                        <MessageSquare className="w-3.5 h-3.5" /> LOGIN TO REVIEW
                                    </button>
                                )}
                            </div>

                            {/* Review Form */}
                            {showReviewForm && (
                                <div className="p-4 md:p-6 rounded-2xl md:rounded-3xl bg-white/[0.03] border border-cyber-cyan/20 space-y-4 md:space-y-5">
                                    <h3 className="text-xs md:text-sm font-cyber font-black text-white uppercase tracking-tight">SUBMIT YOUR REVIEW</h3>

                                    {reviewError && (
                                        <div className="px-4 py-3 rounded-xl bg-cyber-pink/10 border border-cyber-pink/30 text-cyber-pink text-xs font-cyber">
                                            ⚠ {typeof reviewError === 'object' ? (reviewError.message || reviewError.name || JSON.stringify(reviewError)) : reviewError}
                                        </div>
                                    )}

                                    <form onSubmit={formik.handleSubmit} className="space-y-3 md:space-y-4">
                                        {/* Star rating selector */}
                                        <div className="space-y-1 md:space-y-1.5">
                                            <label className="text-[9px] md:text-[10px] font-cyber font-bold text-gray-500 uppercase tracking-widest">Your Rating</label>
                                            <div className="flex items-center gap-1.5 md:gap-2">
                                                {[1, 2, 3, 4, 5].map((star) => {
                                                    const isLit = star <= (hoverRating || formik.values.rating);
                                                    return (
                                                        <button
                                                            key={star}
                                                            type="button"
                                                            onClick={() => formik.setFieldValue('rating', star)}
                                                            onMouseEnter={() => setHoverRating(star)}
                                                            onMouseLeave={() => setHoverRating(0)}
                                                            className="transition-transform hover:scale-125 duration-150 relative group"
                                                        >
                                                            <Star className={`w-5 h-5 md:w-6 md:h-6 transition-all duration-150 ${isLit ? 'fill-cyber-cyan text-cyber-cyan filter drop-shadow-[0_0_6px_rgba(0,240,255,0.5)]' : 'text-gray-700 hover:text-gray-500'}`} />
                                                        </button>
                                                    );
                                                })}
                                                <span className="text-[10px] md:text-xs text-gray-500 font-cyber ml-1.5 md:ml-2">
                                                    {hoverRating ? `Preview: ${hoverRating}` : `Selected: ${formik.values.rating}`}/5
                                                </span>
                                            </div>
                                            {formik.touched.rating && formik.errors.rating && (
                                                <p className="text-cyber-pink text-[9px] font-cyber tracking-wide uppercase mt-0.5 pl-1">⚠ {formik.errors.rating}</p>
                                            )}
                                        </div>

                                        {/* Comment */}
                                        <div className="space-y-1 md:space-y-1.5">
                                            <label className="text-[9px] md:text-[10px] font-cyber font-bold text-gray-500 uppercase tracking-widest">Your Review</label>
                                            <textarea
                                                rows={4}
                                                name="comment"
                                                value={formik.values.comment}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="Share your experience with this game (minimum 10 characters)..."
                                                className={`w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg md:rounded-xl text-xs md:text-sm font-sans text-white placeholder-gray-600 bg-white/[0.04] border focus:outline-none transition-all resize-none
                                                    ${formik.touched.comment && formik.errors.comment
                                                        ? 'border-cyber-pink/45 focus:border-cyber-cyan/50'
                                                        : 'border-white/10 focus:border-cyber-cyan/50'}`}
                                            />
                                            <div className="flex justify-between items-center text-[8px] md:text-[9px] font-cyber mt-1 px-1">
                                                <span className={`uppercase tracking-wider font-bold ${formik.values.comment.trim().length < 10 ? 'text-cyber-pink' : 'text-cyber-green'}`}>
                                                    {formik.values.comment.trim().length === 0 ? 'REQUIRED' : formik.values.comment.trim().length < 10 ? 'MINIMUM 10 CHARACTERS' : 'LOOKS GOOD'}
                                                </span>
                                                <span className={`${formik.values.comment.trim().length < 10 || formik.values.comment.trim().length > 500 ? 'text-cyber-pink font-bold' : 'text-gray-500'}`}>
                                                    {formik.values.comment.trim().length}/500
                                                </span>
                                            </div>
                                            {formik.touched.comment && formik.errors.comment && (
                                                <p className="text-cyber-pink text-[9px] font-cyber tracking-wide uppercase mt-0.5 pl-1">⚠ {formik.errors.comment}</p>
                                            )}
                                        </div>

                                        <div className="flex gap-2.5 pt-1.5 md:pt-2">
                                            <button
                                                type="submit"
                                                disabled={reviewsLoading}
                                                className="cyber-btn flex items-center gap-2 !px-4 md:!px-6 !py-2.5 md:!py-3 text-xs md:text-sm disabled:opacity-50"
                                            >
                                                {reviewsLoading ? (
                                                    <span className="w-4 h-4 border-2 border-cyber-black/30 border-t-cyber-black rounded-full animate-spin"></span>
                                                ) : (
                                                    <><MessageSquare className="w-3.5 h-3.5" /> SUBMIT REVIEW</>
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => { setShowReviewForm(false); setReviewError(''); setHoverRating(0); formik.resetForm(); }}
                                                className="px-4 md:px-5 py-2.5 md:py-3 rounded-lg md:rounded-xl bg-white/5 border border-white/10 text-gray-400 hover:text-white text-[10px] md:text-xs font-cyber font-bold uppercase tracking-wider transition-all"
                                            >
                                                CANCEL
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}

                            {/* Overall Score & Ratings breakdown Card */}
                            <div className="p-4 sm:p-6 md:p-8 rounded-2xl md:rounded-3xl bg-[#0a0a14] border border-white/10 shadow-2xl mb-6 md:mb-10">
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center">

                                    {/* Left: Overall Score Sub-Card */}
                                    <div className="md:col-span-5 p-6 md:p-8 rounded-2xl bg-[#04040a] border border-white/5 flex flex-col items-center justify-center text-center shadow-inner">
                                        <span className="text-[10px] font-cyber font-bold text-gray-400 uppercase tracking-widest mb-3">
                                            OVERALL SCORE
                                        </span>
                                        <div className="flex items-baseline justify-center gap-1 my-1">
                                            <span className="text-5xl sm:text-6xl font-cyber font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyber-pink via-cyber-purple to-cyber-pink drop-shadow-[0_0_20px_rgba(255,0,122,0.4)]">
                                                {averageRating}
                                            </span>
                                            <span className="text-cyber-pink text-xl font-cyber font-bold">
                                                /5
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 my-3">
                                            {[...Array(5)].map((_, i) => (
                                                <Star
                                                    key={i}
                                                    className={`w-4 h-4 sm:w-5 sm:h-5 transition-all ${i < Math.round(averageRating)
                                                        ? 'fill-amber-400 text-amber-400 filter drop-shadow-[0_0_6px_rgba(245,158,11,0.5)]'
                                                        : 'text-gray-700'
                                                        }`}
                                                />
                                            ))}
                                        </div>
                                        {/* <p className="text-xs text-gray-400 font-sans font-medium mt-1">
                                            Based on {totalReviewsCount} {totalReviewsCount === 1 ? 'Review' : 'Reviews'}
                                        </p> */}
                                    </div>

                                    {/* Right: Star Ratings Breakdown Bars */}
                                    <div className="md:col-span-7 space-y-3 sm:space-y-4 px-2 sm:px-4">
                                        {[5, 4, 3, 2, 1].map((star) => (
                                            <div key={star} className="flex items-center gap-3 sm:gap-4 text-xs font-cyber font-semibold group">
                                                <span className="w-14 sm:w-16 text-gray-400 group-hover:text-white transition-colors shrink-0">
                                                    {star} STAR
                                                </span>
                                                <div className="flex-1 h-2 sm:h-2.5 bg-[#141524] rounded-full overflow-hidden border border-white/5 relative">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-cyber-purple via-cyber-blue to-cyber-cyan rounded-full transition-all duration-1000 ease-out shadow-[0_0_12px_rgba(0,240,255,0.4)]"
                                                        style={{ width: `${starPercentages[star]}%` }}
                                                    ></div>
                                                </div>
                                                <span className="w-10 sm:w-12 text-right text-gray-400 font-mono font-medium shrink-0">
                                                    {String(starPercentages[star]).padStart(2, '0')}%
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                </div>
                            </div>

                            {/* <div className="space-y-4 md:space-y-6">
                                {reviews.length > 0 ? (
                                    <>
                                        {reviews.slice(0, visibleReviewsCount).map((rev) => (
                                            <div key={rev._id || rev.id} className="group p-4 md:p-6 rounded-2xl md:rounded-3xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] hover:border-white/10 transition-all">
                                                <div className="flex flex-row justify-between items-center gap-3 mb-3 md:mb-4">
                                                    <div className="flex items-center gap-2.5 md:gap-4 min-w-0">
                                                        <div className="w-8 h-8 md:w-12 md:h-12 rounded-full overflow-hidden border-2 border-cyber-purple/30 group-hover:border-cyber-purple transition-all flex-shrink-0 bg-cyber-purple/10 flex items-center justify-center">
                                                            {rev.user?.photo ? (
                                                                <img src={rev.user.photo} alt={rev.user?.firstName} className="w-full h-full object-cover" />
                                                            ) : (
                                                                <span className="font-cyber font-bold text-cyber-purple text-xs md:text-base uppercase">
                                                                    {rev.user?.firstName?.charAt(0)}{rev.user?.lastName?.charAt(0)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <h4 className="font-cyber font-bold text-xs md:text-sm text-white uppercase tracking-tight truncate max-w-[120px] sm:max-w-none">{rev.user?.firstName} {rev.user?.lastName}</h4>
                                                            <div className="flex flex-wrap items-center gap-1 md:gap-1.5 mt-0.5 md:mt-1">
                                                                <div className="flex items-center gap-0.5">
                                                                    {[...Array(5)].map((_, i) => (
                                                                        <Star key={i} className={`w-2 h-2 md:w-3 md:h-3 ${i < rev.rating ? 'fill-cyber-purple text-cyber-purple' : 'text-gray-800'}`} />
                                                                    ))}
                                                                </div>
                                                                <span className="text-[8px] md:text-[10px] text-gray-600 font-mono hidden sm:inline">{new Date(rev.createdAt).toLocaleDateString()}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 md:gap-3 flex-shrink-0">
                                                        <div className="flex items-center gap-1 group/btn">
                                                            <button
                                                                onClick={() => isAuthenticated ? dispatch(toggleLikeReview(rev._id || rev.id)) : toast.error("Please login to like reviews")}
                                                                className={`p-1 md:p-2 rounded-lg md:rounded-xl transition-all ${rev.likes?.includes(currentUser?._id) ? 'bg-cyber-cyan/10 text-cyber-cyan' : 'bg-white/5 text-gray-500 hover:text-white'}`}
                                                            >
                                                                <ThumbsUp className={`w-2.5 h-2.5 md:w-3.5 md:h-3.5 ${rev.likes?.includes(currentUser?._id) ? 'fill-cyber-cyan' : ''}`} />
                                                            </button>
                                                            <span className="text-[9px] sm:text-[10px] font-mono font-bold text-gray-600 group-hover/btn:text-gray-400">{rev.likes?.length || 0}</span>
                                                        </div>

                                                        <div className="flex items-center gap-1 group/btn">
                                                            <button
                                                                onClick={() => isAuthenticated ? dispatch(toggleDislikeReview(rev._id || rev.id)) : toast.error("Please login to dislike reviews")}
                                                                className={`p-1 md:p-2 rounded-lg md:rounded-xl transition-all ${rev.dislikes?.includes(currentUser?._id) ? 'bg-cyber-pink/10 text-cyber-pink' : 'bg-white/5 text-gray-500 hover:text-white'}`}
                                                            >
                                                                <ThumbsDown className={`w-2.5 h-2.5 md:w-3.5 md:h-3.5 ${rev.dislikes?.includes(currentUser?._id) ? 'fill-cyber-pink' : ''}`} />
                                                            </button>
                                                            <span className="text-[9px] sm:text-[10px] font-mono font-bold text-gray-600 group-hover/btn:text-gray-400">{rev.dislikes?.length || 0}</span>
                                                        </div>

                                                        {(currentUser?._id === rev.user?._id || currentUser?.role === 'admin') && (
                                                            <button
                                                                onClick={() => setDeleteConfirmId(rev._id || rev.id)}
                                                                className="p-1 md:p-2 rounded-lg md:rounded-xl bg-white/5 text-gray-500 hover:text-cyber-pink hover:bg-cyber-pink/5 transition-all">
                                                                <Trash2 className="w-2.5 h-2.5 md:w-3.5 md:h-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                 <p className="text-gray-400 text-xs md:text-sm leading-relaxed pl-0.5 sm:pl-16">
                                                    {rev.comment}
                                                </p>
                                            </div>
                                        ))}

                                        {reviews.length > visibleReviewsCount && (
                                            <div className="flex justify-center pt-4 sm:pt-6">
                                                <button
                                                    onClick={() => setVisibleReviewsCount(prev => prev + 5)}
                                                    className="px-6 py-3.5 sm:px-8 sm:py-4 rounded-xl sm:rounded-2xl bg-white/5 border border-white/10 hover:border-cyber-cyan/40 text-gray-400 hover:text-white text-[10px] sm:text-xs font-cyber font-bold uppercase tracking-widest transition-all hover:bg-cyber-cyan/10 hover:shadow-[0_0_20px_rgba(0,240,255,0.15)] hover:-translate-y-0.5 duration-300"
                                                >
                                                    VIEW MORE REVIEWS
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center py-12 border-2 border-dashed border-white/5 rounded-3xl">
                                        <MessageSquare className="w-8 h-8 text-gray-700 mx-auto mb-4 opacity-20" />
                                        <p className="text-gray-500 font-cyber text-xs uppercase tracking-widest">No reviews yet. Be the first to share your thoughts!</p>
                                    </div>
                                )}
                            </div> */}
                        </section>
                    </div>

                    {/* Sidebar: Purchase Card & Similar Experiences (Order 2 on Mobile/Tablet - shown after Overview) */}
                    <div className="order-2 lg:order-none lg:col-span-5 xl:col-span-4 lg:col-start-8 xl:col-start-9 lg:row-start-1 lg:row-span-2 pt-4 lg:pt-16">
                        <div className="sticky top-28 space-y-4 md:space-y-6">

                            {/* Purchase Card */}
                            <div className="reveal relative rounded-3xl overflow-hidden p-4 sm:p-5 xl:p-6 border border-white/10 bg-gradient-to-b from-[#0e0f22]/95 via-[#080915]/95 to-[#04040a]/98 backdrop-blur-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.85)] group/purchase-card">
                                {/* Cyberpunk Top Glowing Accent Line */}
                                <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyber-cyan via-[#bd00ff] to-transparent opacity-80 group-hover/purchase-card:opacity-100 transition-opacity" />

                                {/* Ambient Cyber Corner Glow */}
                                <div className="absolute -top-16 -right-16 w-36 h-36 bg-cyber-cyan/15 rounded-full blur-3xl pointer-events-none" />
                                <div className="absolute -bottom-16 -left-16 w-36 h-36 bg-cyber-purple/15 rounded-full blur-3xl pointer-events-none" />

                                <div className="relative space-y-4 sm:space-y-5">
                                    {/* Header: License Activation Status + Rating */}
                                    <div className="flex items-center justify-between gap-2 pb-3 border-b border-white/5">
                                        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                                            <span className="relative flex h-2 w-2">
                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyber-green opacity-75"></span>
                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyber-green shadow-[0_0_8px_#39ff14]"></span>
                                            </span>
                                            <span className="text-[10px] sm:text-[11px] font-cyber font-bold tracking-wider text-gray-300 uppercase whitespace-nowrap">
                                                DIGITAL LICENSE
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 rounded-full bg-amber-400/10 border border-amber-400/20 text-amber-300 text-[11px] sm:text-xs font-bold shadow-[0_0_12px_rgba(251,191,36,0.15)] shrink-0 whitespace-nowrap">
                                            <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-amber-400 text-amber-400" />
                                            <span>{averageRating}</span>
                                            {/* <span className="text-gray-400 text-[10px] font-normal">({totalReviewsCount})</span> */}
                                        </div>
                                    </div>

                                    {/* Platform Selector */}
                                    {(() => {
                                        const platformList = [
                                            { key: 'windows', label: 'PC', fullName: 'Windows PC', icon: FaWindows, color: '#00f0ff' },
                                            { key: 'playstation', label: 'PS', fullName: 'PlayStation', icon: SiPlaystation, color: '#0070d1' },
                                            { key: 'xbox', label: 'Xbox', fullName: 'Xbox Series X|S', icon: FaXbox, color: '#107c10' },
                                            { key: 'nintendo', label: 'Switch', fullName: 'Nintendo Switch', icon: BsNintendoSwitch, color: '#e60012' },
                                            { key: 'quest', label: 'Quest', fullName: 'Meta Quest VR', icon: SiOculus, color: '#a855f7' },
                                            { key: 'android', label: 'Android', fullName: 'Android OS', icon: DiAndroid, color: '#3ddc84' },
                                            { key: 'ios', label: 'iOS', fullName: 'Apple iOS', icon: FaApple, color: '#e2e8f0' },
                                            { key: 'visionpro', label: 'Vision Pro', fullName: 'Apple Vision Pro', icon: TbDeviceVisionProFilled, color: '#c084fc' },
                                            { key: 'steamdeck', label: 'Steam Deck', fullName: 'Steam Deck', icon: SiSteamdeck, color: '#38bdf8' }
                                        ];
                                        const availablePlatforms = platformList.filter(p => game?.platforms?.[p.key]?.available);
                                        const activePlatformInfo = platformList.find(p => p.key === selectedPlatform) || availablePlatforms[0];

                                        const layoutClass = availablePlatforms.length === 1
                                            ? 'flex items-center justify-start'
                                            : availablePlatforms.length === 2
                                                ? 'grid grid-cols-2 gap-2 sm:gap-2.5'
                                                : availablePlatforms.length === 3
                                                    ? 'grid grid-cols-3 gap-2 sm:gap-2.5'
                                                    : availablePlatforms.length === 4
                                                        ? 'grid grid-cols-4 gap-1.5 sm:gap-2'
                                                        : (availablePlatforms.length === 5 || availablePlatforms.length === 6)
                                                            ? 'grid grid-cols-3 gap-2 sm:gap-2.5'
                                                            : 'grid grid-cols-4 gap-1.5 sm:gap-2';

                                        const buttonWidthClass = availablePlatforms.length === 1
                                            ? 'w-24 sm:w-28'
                                            : 'w-full';

                                        return (
                                            <div className="space-y-2.5 sm:space-y-3">
                                                <div className="flex items-center justify-between gap-1.5 flex-wrap">
                                                    <p className="text-[10px] sm:text-xs font-cyber font-bold text-gray-300 tracking-wider flex items-center gap-1.5 shrink-0">
                                                        PLATFORM LICENSE
                                                    </p>
                                                    <span
                                                        className="text-[10px] sm:text-[11px] font-bold tracking-wider px-2 py-0.5 rounded-lg border shrink-0 transition-all duration-300 truncate max-w-[130px] sm:max-w-none"
                                                        style={{
                                                            color: activePlatformInfo?.color || '#00ffff',
                                                            borderColor: `${activePlatformInfo?.color || '#00ffff'}40`,
                                                            backgroundColor: `${activePlatformInfo?.color || '#00ffff'}18`
                                                        }}
                                                    >
                                                        {activePlatformInfo?.fullName || selectedPlatform?.toUpperCase()}
                                                    </span>
                                                </div>

                                                {/* Square Platform Buttons - Dynamic Responsive Grid */}
                                                <div className={`w-full ${layoutClass} p-1 sm:p-1.5 rounded-2xl`}>
                                                    {availablePlatforms.map((plat) => {
                                                        const isSelected = selectedPlatform === plat.key;
                                                        const Icon = plat.icon;
                                                        return (
                                                            <button
                                                                key={plat.key}
                                                                type="button"
                                                                onClick={() => setSelectedPlatform(plat.key)}
                                                                style={isSelected ? {
                                                                    borderColor: plat.color,
                                                                    background: `radial-gradient(circle at 50% 30%, ${plat.color}28, ${plat.color}0a 85%)`,
                                                                    boxShadow: `0 0 20px ${plat.color}35, inset 0 0 12px ${plat.color}15`
                                                                } : {}}
                                                                className={`relative ${buttonWidthClass} h-[64px] sm:h-[74px] p-1 sm:p-2 flex flex-col items-center justify-center gap-1 rounded-xl text-[10px] sm:text-[11px] font-cyber font-bold transition-all duration-200 group/btn ${isSelected
                                                                    ? 'text-white border'
                                                                    : 'bg-[#0d0e1b]/95 text-gray-400 hover:text-white border border-white/10 hover:border-white/25 hover:bg-[#14162a]'
                                                                    }`}
                                                            >
                                                                <Icon
                                                                    size={20}
                                                                    style={{ color: plat.color }}
                                                                    className="transition-transform duration-200 group-hover/btn:scale-110 sm:scale-110"
                                                                />
                                                                <span
                                                                    className="text-[9.5px] sm:text-[11px] tracking-wider font-bold transition-colors text-center truncate max-w-full leading-tight"
                                                                    style={{ color: isSelected ? plat.color : undefined }}
                                                                >
                                                                    {plat.label}
                                                                </span>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Price Display Module */}
                                    {(() => {
                                        const platformPrice = game?.platforms?.[selectedPlatform]?.price !== undefined
                                            ? game.platforms[selectedPlatform].price
                                            : game.price;
                                        const platformOriginal = game?.platforms?.[selectedPlatform]?.originalPrice !== undefined
                                            ? game.platforms[selectedPlatform].originalPrice
                                            : game.originalPrice || game.price;
                                        const hasDiscount = game.discount > 0 && Number(platformOriginal) > Number(platformPrice);
                                        const savingsAmount = hasDiscount ? (Number(platformOriginal) - Number(platformPrice)).toFixed(2) : null;
                                        const isFree = platformPrice === 0 || platformPrice === '0';

                                        return (
                                            <div className="p-3.5 sm:p-4 rounded-2xl bg-[#060713]/90 border border-white/10 relative overflow-hidden group/pricing">
                                                <div className="absolute -top-10 -right-10 w-28 h-28 bg-cyber-cyan/10 rounded-full blur-2xl pointer-events-none" />

                                                {hasDiscount && (
                                                    <div className="flex items-center justify-end mb-2">
                                                        <span className="px-2.5 py-0.5 rounded-full bg-gradient-to-r from-cyber-pink/20 to-rose-500/20 border border-cyber-pink/40 text-cyber-pink text-[10px] font-extrabold tracking-wider animate-pulse">
                                                            PROMO: -{game.discount}% OFF
                                                        </span>
                                                    </div>
                                                )}

                                                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                                    <div className="flex items-baseline gap-2.5">
                                                        <h2 className="text-3xl xl:text-4xl font-cyber font-black tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-cyber-cyan drop-shadow-[0_0_15px_rgba(0,240,255,0.35)] leading-none">
                                                            {isFree ? 'FREE' : `$${Number(platformPrice).toFixed(2)}`}
                                                        </h2>
                                                        {hasDiscount && (
                                                            <span className="text-base text-gray-500 line-through font-semibold">
                                                                ${Number(platformOriginal).toFixed(2)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {savingsAmount && (
                                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-cyber-green/15 border border-cyber-green/30 text-cyber-green tracking-wide">
                                                            Save ${savingsAmount}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* Action Buttons (Perfected Neon Cyber Design) */}
                                    <div className="space-y-2.5">
                                        <button
                                            type="button"
                                            onClick={() => setShowBuyNowModal(true)}
                                            disabled={restricted}
                                            className={`relative overflow-hidden w-full h-12 sm:h-13 flex items-center justify-center gap-2.5 px-5 rounded-2xl font-cyber font-bold text-xs sm:text-sm uppercase tracking-widest transition-all duration-300 whitespace-nowrap group ${restricted
                                                ? 'bg-[#0a0c1a]/95 border border-white/10 text-gray-300/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_4px_12px_rgba(0,0,0,0.6)] cursor-not-allowed select-none'
                                                : 'bg-gradient-to-r from-[#00efff] via-[#5c60ff] to-[#bd00ff] text-black font-black shadow-[0_0_25px_rgba(0,239,255,0.35),0_0_25px_rgba(189,0,255,0.3)] hover:shadow-[0_0_35px_rgba(0,239,255,0.6),0_0_30px_rgba(189,0,255,0.5)] hover:scale-[1.01] active:scale-[0.98]'
                                                }`}
                                        >
                                            {!restricted && (
                                                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/35 to-transparent pointer-events-none" />
                                            )}
                                            <Zap className={`w-4 h-4 shrink-0 ${restricted ? 'text-cyan-400/60' : 'text-black fill-black'}`} />
                                            <span className="tracking-widest">{restricted ? 'PURCHASE PAUSED' : 'BUY NOW'}</span>
                                            {!restricted && (
                                                <ArrowRight className="w-4 h-4 shrink-0 text-black font-black transition-transform duration-300 group-hover:translate-x-1 stroke-[2.5]" />
                                            )}
                                        </button>

                                        <div className="flex items-center gap-2.5">
                                            <button
                                                type="button"
                                                onClick={() => dispatch(addToCart({ ...game, selectedPlatform }))}
                                                disabled={restricted}
                                                className={`relative overflow-hidden flex-1 min-w-0 h-11 sm:h-12 flex items-center justify-center gap-2 px-4 rounded-2xl font-cyber uppercase tracking-wider transition-colors duration-200 whitespace-nowrap select-none group ${restricted
                                                    ? 'bg-gradient-to-r from-[#008ba3] via-[#3d42b3] to-[#6d0d96] text-black font-black text-[11px] sm:text-xs border-0 border-transparent shadow-none outline-none ring-0 cursor-not-allowed select-none'
                                                    : isInCart
                                                        ? 'bg-emerald-500/20 hover:bg-emerald-500/25 border-2 border-emerald-400 text-emerald-300 font-extrabold text-[11px] sm:text-xs shadow-none'
                                                        : 'bg-gradient-to-r from-[#0d163a]/95 via-[#18113a]/95 to-[#0d163a]/95 hover:from-[#132155] hover:via-[#221650] hover:to-[#132155] border-2 border-cyan-400/80 hover:border-cyan-300 text-cyan-200 hover:text-white font-extrabold text-[11px] sm:text-xs shadow-none'
                                                    }`}
                                            >
                                                {!restricted && (
                                                    <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/25 to-transparent pointer-events-none" />
                                                )}
                                                {restricted ? (
                                                    <ShoppingCart className="w-4 h-4 shrink-0 text-black fill-black" />
                                                ) : isInCart ? (
                                                    <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
                                                ) : (
                                                    <ShoppingCart className="w-4 h-4 shrink-0 text-cyan-400" />
                                                )}
                                                <span className="tracking-wider">
                                                    {restricted ? 'COMING SOON' : isInCart ? 'IN CART' : 'ADD TO CART'}
                                                </span>
                                            </button>

                                            <button
                                                type="button"
                                                onClick={() => dispatch(toggleWishlist(gameId))}
                                                className={`relative w-12 sm:w-14 h-11 sm:h-12 shrink-0 rounded-2xl border transition-all duration-300 flex items-center justify-center group active:scale-95 ${isFav
                                                    ? 'bg-cyber-pink/20 border-cyber-pink/60 text-cyber-pink shadow-[0_0_20px_rgba(255,0,128,0.35)]'
                                                    : 'bg-[#080918]/90 hover:bg-[#140f24] border-white/10 hover:border-cyber-pink/50 text-gray-400 hover:text-cyber-pink shadow-[0_0_15px_rgba(255,0,128,0.05)] hover:shadow-[0_0_20px_rgba(255,0,128,0.2)]'
                                                    }`}
                                                title={isFav ? "Remove from Wishlist" : "Add to Wishlist"}
                                                aria-label="Wishlist"
                                            >
                                                <Heart className={`w-4 h-4 transition-transform duration-300 group-hover:scale-110 ${isFav ? 'fill-cyber-pink text-cyber-pink drop-shadow-[0_0_8px_rgba(255,0,128,0.6)]' : 'text-gray-300 group-hover:text-cyber-pink'
                                                    }`} />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Value Props */}
                                    <div className="pt-3.5 border-t border-white/5">
                                        <div className="grid grid-cols-3 gap-1.5 sm:gap-2.5">
                                            <div className="p-2 sm:p-2.5 rounded-xl sm:rounded-2xl bg-[#08091a] border border-white/10 hover:border-cyber-cyan/40 flex flex-col items-center justify-center text-center gap-1 group transition-all duration-300 hover:bg-[#0e102e] shadow-sm">
                                                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl bg-cyber-cyan/15 border border-cyber-cyan/30 flex items-center justify-center text-cyber-cyan shadow-[0_0_12px_rgba(0,240,255,0.2)] group-hover:scale-110 transition-transform">
                                                    <Zap className="w-3 h-3 sm:w-4 sm:h-4 fill-cyber-cyan/20 text-cyber-cyan" />
                                                </div>
                                                <div className="space-y-0.5 w-full overflow-hidden">
                                                    <span className="text-[9.5px] min-[360px]:text-[10.5px] sm:text-xs font-cyber font-bold text-white block leading-tight truncate">Instant Key</span>
                                                    <span className="text-[8px] min-[360px]:text-[9px] sm:text-[10px] font-cyber font-medium text-gray-400 block leading-none truncate">Automated</span>
                                                </div>
                                            </div>

                                            <div className="p-2 sm:p-2.5 rounded-xl sm:rounded-2xl bg-[#08091a] border border-white/10 hover:border-cyber-purple/40 flex flex-col items-center justify-center text-center gap-1 group transition-all duration-300 hover:bg-[#0e102e] shadow-sm">
                                                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl bg-cyber-purple/15 border border-cyber-purple/30 flex items-center justify-center text-cyber-purple shadow-[0_0_12px_rgba(189,0,255,0.2)] group-hover:scale-110 transition-transform">
                                                    <Shield className="w-3 h-3 sm:w-4 sm:h-4 fill-cyber-purple/20 text-cyber-purple" />
                                                </div>
                                                <div className="space-y-0.5 w-full overflow-hidden">
                                                    <span className="text-[9.5px] min-[360px]:text-[10.5px] sm:text-xs font-cyber font-bold text-white block leading-tight truncate">Secure Pay</span>
                                                    <span className="text-[8px] min-[360px]:text-[9px] sm:text-[10px] font-cyber font-medium text-gray-400 block leading-none truncate">256-Bit SSL</span>
                                                </div>
                                            </div>

                                            <div className="p-2 sm:p-2.5 rounded-xl sm:rounded-2xl bg-[#08091a] border border-white/10 hover:border-cyber-pink/40 flex flex-col items-center justify-center text-center gap-1 group transition-all duration-300 hover:bg-[#0e102e] shadow-sm">
                                                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl bg-cyber-pink/15 border border-cyber-pink/30 flex items-center justify-center text-cyber-pink shadow-[0_0_12px_rgba(255,0,122,0.2)] group-hover:scale-110 transition-transform">
                                                    <Globe className="w-3 h-3 sm:w-4 sm:h-4 text-cyber-pink" />
                                                </div>
                                                <div className="space-y-0.5 w-full overflow-hidden">
                                                    <span className="text-[9.5px] min-[360px]:text-[10.5px] sm:text-xs font-cyber font-bold text-white block leading-tight truncate">Global Key</span>
                                                    <span className="text-[8px] min-[360px]:text-[9px] sm:text-[10px] font-cyber font-medium text-gray-400 block leading-none truncate">Region Free</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Similar Experiences */}
                            {relatedGames?.length > 0 && (
                                <div className="reveal space-y-3 pt-2">
                                    <h3 className="text-[10px] sm:text-xs font-cyber font-black text-gray-400 uppercase tracking-widest px-2 flex items-center justify-between">
                                        <span>SIMILAR EXPERIENCES</span>
                                        <span className="text-cyber-cyan text-[10px] font-normal">({relatedGames.length})</span>
                                    </h3>
                                    <div className="space-y-2.5">
                                        {relatedGames.map(g => {
                                            const rId = g._id || g.id;
                                            return (
                                                <div
                                                    key={rId}
                                                    onClick={() => navigate(getGamePath(g))}
                                                    className="group relative flex items-center gap-3 p-2.5 rounded-2xl bg-[#0a0c16]/95 border border-white/10 hover:border-cyber-cyan/50 hover:shadow-[0_0_20px_rgba(0,240,255,0.12)] cursor-pointer transition-all duration-300"
                                                >
                                                    <div className="w-20 h-14 rounded-xl overflow-hidden shrink-0 border border-white/10 bg-[#05060f] relative">
                                                        <img src={g.cover_image?.url || g.image} alt={g.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between text-[10px] text-gray-400 mb-0.5">
                                                            <span className="truncate font-cyber text-gray-400">{g.category?.name || 'Action'}</span>
                                                            <div className="flex items-center gap-1 shrink-0">
                                                                {g.platforms?.playstation?.available && <SiPlaystation size={10} className="text-cyber-blue" />}
                                                                {g.platforms?.windows?.available && <FaWindows size={10} className="text-[#0078d7]" />}
                                                            </div>
                                                        </div>
                                                        <h4 className="text-xs sm:text-sm font-cyber font-bold text-white uppercase tracking-wide truncate group-hover:text-cyber-cyan transition-colors" title={g.title}>{g.title}</h4>
                                                        <p className="text-xs sm:text-sm text-cyber-cyan font-cyber font-bold mt-0.5">
                                                            {Number(g.price) === 0 ? 'FREE' : `$${Number(g.price || 0).toFixed(2)}`}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>

                </div>
            </div>

            {/* Recently Viewed & Recommended */}
            <RecentlyViewed />

            {/* Direct Buy Now Modal */}
            <BuyNowModal
                isOpen={showBuyNowModal}
                onClose={() => setShowBuyNowModal(false)}
                game={game}
                selectedPlatform={selectedPlatform}
            />

            {/* CSS for animations */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fade-in {
                    animation: fade-in 0.6s cubic-bezier(0.16, 1, 0.3, 1);
                }
            ` }} />
        </div>
    );
}
